import { useId } from "react";
import { Heading as AriaHeading } from "react-aria-components";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import type { ticketAdminFormPaths } from "@/features/form-editor/admin-form-editor-i18n-paths";
import { useAppTranslation } from "@/hooks/use-app-translation";

export interface FormEditorUnsavedChangesModalProps {
	isOpen: boolean;
	isApplying: boolean;
	isSaveInFlight?: boolean;
	paths: typeof ticketAdminFormPaths;
	onStay: () => void;
	onDiscard: () => void;
	onApply: () => void;
}

export function FormEditorUnsavedChangesModal({
	isOpen,
	isApplying,
	isSaveInFlight = false,
	paths,
	onStay,
	onDiscard,
	onApply,
}: FormEditorUnsavedChangesModalProps) {
	const { t } = useAppTranslation();
	const titleId = useId();
	const descriptionId = useId();
	const leaveActionsDisabled = isApplying || isSaveInFlight;

	return (
		<ModalOverlay isOpen={isOpen} isDismissable={false} isKeyboardDismissDisabled>
			<Modal>
				<Dialog aria-labelledby={titleId} aria-describedby={descriptionId}>
					<div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-lg">
						<div className="flex flex-col gap-1 px-4 pt-5 sm:px-6 sm:pt-6">
							<AriaHeading
								id={titleId}
								slot="title"
								className="text-md font-semibold text-primary"
							>
								{t(paths.formEditor.unsaved.title)}
							</AriaHeading>
							<p id={descriptionId} className="text-sm text-tertiary">
								{t(paths.formEditor.unsaved.body)}
							</p>
						</div>
						<div className="flex flex-col-reverse gap-3 border-t border-secondary p-4 sm:grid sm:grid-cols-3 sm:p-6">
							<Button
								color="secondary"
								size="lg"
								onClick={onStay}
								isDisabled={leaveActionsDisabled}
							>
								{t(paths.formEditor.unsaved.stay)}
							</Button>
							<Button
								color="secondary-destructive"
								size="lg"
								onClick={onDiscard}
								isDisabled={leaveActionsDisabled}
							>
								{t(paths.formEditor.unsaved.discard)}
							</Button>
							<Button
								color="primary"
								size="lg"
								onClick={onApply}
								isLoading={isApplying}
								isDisabled={leaveActionsDisabled}
								showTextWhileLoading
							>
								{t("common.actions.apply")}
							</Button>
						</div>
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
}
